import { property } from "@/utils/unknown";

/**
 * Subdirectories of Electron `app.getPath("userData")` (Application Support
 * on macOS). Scratch the OS may purge — Cocoa "Saved Application State" —
 * must not be used for these.
 */
export const USER_DATA_LOGS_DIR = "logs";
export const USER_DATA_RECORDINGS_DIR = "recordings";

export function isFileSystemError(error: unknown, code: string): boolean {
  return property(error, "code") === code;
}

function nodeFs(): typeof import("node:fs") {
  return require("node:fs");
}

function nodePath(): typeof import("node:path") {
  return require("node:path");
}

/** Create `dirPath` and any missing parents. */
export async function ensureDir(dirPath: string): Promise<void> {
  await nodeFs().promises.mkdir(dirPath, { recursive: true });
}

/** Create the parent directory of `filePath` if it is missing. */
export async function ensureParentDir(filePath: string): Promise<void> {
  await ensureDir(nodePath().dirname(filePath));
}

export type WriteEnsuringDirFlag = "w" | "a";

/**
 * Write (or append) `data` to `filePath`, creating missing parent directories.
 *
 * Tries the write first (the common case: the directory already exists). On
 * `ENOENT` — missing parent, or the directory vanished mid-session — mkdir
 * then retry once. Other errors propagate.
 */
export async function writeEnsuringDir(
  filePath: string,
  data: string | Uint8Array,
  flag: WriteEnsuringDirFlag = "w"
): Promise<void> {
  const fsp = nodeFs().promises;
  const write = () =>
    typeof data === "string"
      ? fsp.writeFile(filePath, data, { encoding: "utf8", flag })
      : fsp.writeFile(filePath, data, { flag });
  try {
    await write();
  } catch (error) {
    if (!isFileSystemError(error, "ENOENT")) throw error;
    await ensureParentDir(filePath);
    await write();
  }
}

/** Delete `filePath` if it exists; ignore a missing file. */
export async function unlinkIfExists(filePath: string): Promise<void> {
  try {
    await nodeFs().promises.unlink(filePath);
  } catch (error) {
    if (!isFileSystemError(error, "ENOENT")) throw error;
  }
}
